import { Anchor, Box, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuReceipt, LuTriangleAlert } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { MoneyText } from '@/frontend/components/marina/MoneyText'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type InvoiceStatus, useInvoiceStatusCounts, useInvoices } from '@/frontend/hooks/useInvoices'

type TabValue = 'ALL' | InvoiceStatus

const VALID: TabValue[] = ['ALL', 'PRO_FORMA', 'DRAFT', 'SENT', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED']

export const Route = createFileRoute('/dashboard/billing/invoices/')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabValue } => ({
    tab: VALID.includes(s.tab as TabValue) ? (s.tab as TabValue) : undefined,
  }),
  component: InvoicesList,
})

function InvoicesList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCanWrite()
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'ALL'
  const setTab = (v: TabValue) => navigate({ search: () => ({ tab: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useInvoices({
    q: query,
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  })
  const { data: countsData } = useInvoiceStatusCounts()
  const counts = countsData?.counts ?? {}
  const totalAll = Object.values(counts).reduce((s, n) => s + n, 0)
  const overdueCount = counts.OVERDUE ?? 0

  const items = data?.items ?? []
  const total = data?.total ?? 0

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuReceipt size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.billing.invoices.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.billing.invoices.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/billing/invoices/new' as any })}>
              {t('page.billing.invoices.btn_new')}
            </Button>
          )}
        </Group>

        {overdueCount > 0 && (
          <Box
            px="md"
            py="xs"
            style={{
              borderRadius: 10,
              background: 'light-dark(var(--mantine-color-red-0), rgba(200,60,60,0.1))',
              cursor: 'pointer',
            }}
            onClick={() => setTab('OVERDUE')}
          >
            <Group gap="xs" justify="space-between">
              <Group gap="xs">
                <ThemeIcon variant="light" color="red" size="sm" radius="xl">
                  <LuTriangleAlert size={13} />
                </ThemeIcon>
                <Text size="sm" fw={600} c="red.7">
                  {t('page.billing.invoices.alert_overdue', { n: overdueCount })}
                </Text>
              </Group>
              <Text size="xs" c="red.6" fw={500}>
                {t('common.see_more')}
              </Text>
            </Group>
          </Box>
        )}

        <Group justify="space-between" wrap="wrap" gap="sm">
          <PillNav
            value={tab}
            onChange={(v) => {
              setTab(v)
              setPage(1)
            }}
            items={[
              {
                value: 'ALL',
                label: t('page.billing.invoices.tab_all'),
                count: totalAll || undefined,
                countColor: 'gray',
              },
              {
                value: 'DRAFT',
                label: t('page.billing.invoices.tab_draft'),
                count: counts.DRAFT || undefined,
                countColor: 'gray',
              },
              {
                value: 'SENT',
                label: t('page.billing.invoices.tab_sent'),
                count: counts.SENT || undefined,
                countColor: 'blue',
              },
              {
                value: 'PARTIALLY_PAID',
                label: t('page.billing.invoices.tab_partial'),
                count: counts.PARTIALLY_PAID || undefined,
                countColor: 'orange',
              },
              {
                value: 'PAID',
                label: t('page.billing.invoices.tab_paid'),
                count: counts.PAID || undefined,
                countColor: 'teal',
              },
              {
                value: 'OVERDUE',
                label: t('page.billing.invoices.tab_overdue'),
                count: overdueCount || undefined,
                countColor: 'red',
              },
              {
                value: 'CANCELLED',
                label: t('page.billing.invoices.tab_cancelled'),
                count: counts.CANCELLED || undefined,
                countColor: 'red',
              },
            ]}
          />
          <input
            type="search"
            value={search}
            placeholder={t('page.billing.invoices.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(search)
                setPage(1)
              }
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 220,
              outline: 'none',
            }}
          />
        </Group>

        <Card radius="md" padding="lg">
          <DataTable
            isEmpty={!isLoading && items.length === 0}
            emptyLabel={t('page.billing.invoices.empty')}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            columns={
              <>
                <Table.Th>{t('page.billing.invoices.col_code')}</Table.Th>
                <Table.Th>{t('page.billing.invoices.col_customer')}</Table.Th>
                <Table.Th>{t('nav.boats')}</Table.Th>
                <Table.Th ta="right">{t('page.billing.invoices.col_total')}</Table.Th>
                <Table.Th>{t('page.billing.invoices.col_due')}</Table.Th>
                <Table.Th>{t('page.billing.invoices.col_status')}</Table.Th>
                <Table.Th ta="right">{t('page.billing.invoices.col_actions')}</Table.Th>
              </>
            }
          >
            {items.map((inv) => {
              const name = inv.customer ? `${inv.customer.firstName} ${inv.customer.lastName}`.trim() : '—'
              const isOverdue = inv.status === 'OVERDUE'
              return (
                <Table.Tr
                  key={inv.id}
                  style={{
                    cursor: 'pointer',
                    background: isOverdue ? 'light-dark(var(--mantine-color-red-0), rgba(200,60,60,0.05))' : undefined,
                  }}
                  onClick={() => navigate({ to: `/dashboard/billing/invoices/${inv.id}` as any })}
                >
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {inv.code}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {dayjs(inv.issueDate).format('DD MMM YYYY')}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {inv.customer?.email ?? '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{inv.boat?.name ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <MoneyText
                      amount={inv.totalIdr}
                      currency={inv.currency}
                      fw={600}
                      size="sm"
                      c={isOverdue ? 'red.6' : undefined}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={isOverdue ? 'red.6' : undefined}>
                      {inv.dueDate ? dayjs(inv.dueDate).format('DD MMM YYYY') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={inv.status} kind="invoice" />
                  </Table.Td>
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Anchor
                      component={Link}
                      to={`/dashboard/billing/invoices/${inv.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {inv.code} →
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </DataTable>
        </Card>
      </Stack>
    </Container>
  )
}
